import type { SeatLayoutSeat } from "../../../api/services/seat-layout.service";
import type {
  LayoutCanvasViewport,
  LayoutSeatPosition,
  LayoutSelectionRect,
  LayoutStudioDraft,
} from "./types";

export type SeatAlignmentMode = "left" | "h-center" | "right" | "top" | "v-center" | "bottom";
export type SeatDistributionMode = "horizontal" | "vertical";

export const roundCanvasValue = (value: number) => Number(value.toFixed(2));

export const clampZoomLevel = (zoomLevel: number) => Math.max(0.5, Math.min(zoomLevel, 3));

export const getFitScale = (draft: LayoutStudioDraft | null, viewport: LayoutCanvasViewport) => {
  if (!draft || !viewport.width || !viewport.height) {
    return 1;
  }

  const widthScale = (viewport.width - 48) / draft.canvas_width;
  const heightScale = (viewport.height - 48) / draft.canvas_height;
  return Math.max(0.2, Math.min(widthScale, heightScale, 1));
};

export const getCanvasBasePosition = (draft: LayoutStudioDraft | null, viewport: LayoutCanvasViewport, scale: number) => {
  if (!draft) {
    return { x: 0, y: 0 };
  }

  if (!viewport.width || !viewport.height) {
    return { x: 24, y: 24 };
  }

  return {
    x: (viewport.width - draft.canvas_width * scale) / 2,
    y: (viewport.height - draft.canvas_height * scale) / 2,
  };
};

export const normalizeSelectionRect = (rect: LayoutSelectionRect) => ({
  x: rect.width < 0 ? rect.x + rect.width : rect.x,
  y: rect.height < 0 ? rect.y + rect.height : rect.y,
  width: Math.abs(rect.width),
  height: Math.abs(rect.height),
});

export const seatIntersectsRect = (seat: Pick<SeatLayoutSeat, "x" | "y" | "width" | "height">, rect: ReturnType<typeof normalizeSelectionRect>) => {
  const seatRight = seat.x + seat.width;
  const seatBottom = seat.y + seat.height;
  const rectRight = rect.x + rect.width;
  const rectBottom = rect.y + rect.height;

  return seat.x < rectRight && seatRight > rect.x && seat.y < rectBottom && seatBottom > rect.y;
};

export const getSeatBounds = (seats: Pick<SeatLayoutSeat, "x" | "y" | "width" | "height">[]) => {
  if (!seats.length) {
    return null;
  }

  const left = Math.min(...seats.map((seat) => seat.x));
  const top = Math.min(...seats.map((seat) => seat.y));
  const right = Math.max(...seats.map((seat) => seat.x + seat.width));
  const bottom = Math.max(...seats.map((seat) => seat.y + seat.height));

  return {
    left,
    top,
    right,
    bottom,
    centerX: (left + right) / 2,
    centerY: (top + bottom) / 2,
  };
};

export const alignSeats = <T extends Pick<SeatLayoutSeat, "x" | "y" | "width" | "height">>(
  seats: T[],
  mode: SeatAlignmentMode,
) => {
  if (seats.length < 2) {
    return seats;
  }

  const bounds = getSeatBounds(seats);
  if (!bounds) {
    return seats;
  }

  return seats.map((seat) => {
    if (mode === "left") {
      return { ...seat, x: roundCanvasValue(bounds.left) };
    }
    if (mode === "h-center") {
      return { ...seat, x: roundCanvasValue(bounds.centerX - seat.width / 2) };
    }
    if (mode === "right") {
      return { ...seat, x: roundCanvasValue(bounds.right - seat.width) };
    }
    if (mode === "top") {
      return { ...seat, y: roundCanvasValue(bounds.top) };
    }
    if (mode === "v-center") {
      return { ...seat, y: roundCanvasValue(bounds.centerY - seat.height / 2) };
    }
    return { ...seat, y: roundCanvasValue(bounds.bottom - seat.height) };
  });
};

export const distributeSeats = <T extends Pick<SeatLayoutSeat, "x" | "y" | "width" | "height">>(
  seats: T[],
  mode: SeatDistributionMode,
) => {
  if (seats.length < 3) {
    return seats;
  }

  const sortedSeats = [...seats].sort((leftSeat, rightSeat) =>
    mode === "horizontal" ? leftSeat.x - rightSeat.x : leftSeat.y - rightSeat.y,
  );
  const bounds = getSeatBounds(sortedSeats);
  if (!bounds) {
    return seats;
  }

  if (mode === "horizontal") {
    const totalWidth = sortedSeats.reduce((sum, seat) => sum + seat.width, 0);
    const spacing = (bounds.right - bounds.left - totalWidth) / (sortedSeats.length - 1);
    let nextX = bounds.left;
    return sortedSeats.map((seat) => {
      const nextSeat = { ...seat, x: roundCanvasValue(nextX) };
      nextX += seat.width + spacing;
      return nextSeat;
    });
  }

  const totalHeight = sortedSeats.reduce((sum, seat) => sum + seat.height, 0);
  const spacing = (bounds.bottom - bounds.top - totalHeight) / (sortedSeats.length - 1);
  let nextY = bounds.top;
  return sortedSeats.map((seat) => {
    const nextSeat = { ...seat, y: roundCanvasValue(nextY) };
    nextY += seat.height + spacing;
    return nextSeat;
  });
};

export const buildMultiSeatDragPreview = ({
  seats,
  draggedSeat,
  dragSnapshot,
  nextPosition,
}: {
  seats: Pick<SeatLayoutSeat, "spot" | "x" | "y">[];
  draggedSeat: Pick<SeatLayoutSeat, "spot" | "x" | "y">;
  dragSnapshot: Map<number, LayoutSeatPosition>;
  nextPosition: LayoutSeatPosition;
}) => {
  const origin = dragSnapshot.get(draggedSeat.spot);
  if (!origin) {
    return {
      [draggedSeat.spot]: nextPosition,
    };
  }

  const deltaX = nextPosition.x - origin.x;
  const deltaY = nextPosition.y - origin.y;

  return Object.fromEntries(
    seats.map((seat) => {
      const selectedOrigin = dragSnapshot.get(seat.spot) || {
        x: seat.x,
        y: seat.y,
      };
      return [
        seat.spot,
        {
          x: roundCanvasValue(selectedOrigin.x + deltaX),
          y: roundCanvasValue(selectedOrigin.y + deltaY),
        },
      ];
    }),
  ) as Record<number, LayoutSeatPosition>;
};
